import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Login from './components/Login';
import SignUp from './components/SignUp';
import GoogleAuthCallback from './components/auth/GoogleAuthCallback';
import FacebookAuthCallback from './components/auth/FacebookAuthCallback';
import HomePage from './components/HomePage';
import AppShell from './components/AppShell';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import FloatingChat from './components/FloatingChat';
import ContentsPage from './pages/Contents';
import VideosPage from './pages/Videos';
import EditImagePage from './pages/EditImage';
import DashboardPage from './pages/Dashboard';
import SchedulingPage from './pages/Scheduling';
import CampaignsPage from './pages/Campaigns';
import BrandIdentityPage from './pages/BrandIdentity';
import TrainAIPage from './pages/TrainAI';
import SettingsPage from './pages/Settings';
import PlansPage from './pages/Plans';
import HelpPage from './pages/Help';
import ManualCreatePage from './pages/ManualCreate';
import CampaignLaunchPage from './pages/CampaignLaunch';
import CampaignDashboardPage from './pages/CampaignDashboard';
import MPFlowPage from './pages/MPFlow';
import MPDashboardPage from './pages/MPDashboard';
import SpecialistPanelPage from './pages/SpecialistPanel';
import CRMPanelPage from './pages/CRMPanel';
import LandingPageBuilderPage from './pages/LandingPageBuilder';
import AdminNicheConfigPage from './pages/AdminNicheConfig';
import CampaignFlowPage from './pages/CampaignFlow';
import CampaignsDashboardPage from './pages/CampaignsDashboard';
import JourneyMapPage from './pages/JourneyMap';
import CreativeChatPage from './pages/CreativeChat';
import LandingPage from './pages/Landing';
import Venda3xPage from './pages/Venda3x';
import MetaAdsPage from './pages/MetaAds';
import AdsManagerPage from './pages/AdsManager';
import AdsCreatePage from './pages/AdsManager/AdsCreate';
import AdsConnectPage from './pages/AdsManager/AdsConnect';
import SignupGatePage from './pages/SignupGate';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TooltipProvider } from './ui/Tooltip/TooltipProvider';
import './design-system/index.css';
import './App.css';
import './ui/components.css';
import './styles/design-generator.css';

function AppContent() {
  const { isLoggedIn, authToken, logout } = useAuth();

  const ProtectedRoute = ({ children }) => {
    if (!isLoggedIn) return <Navigate to="/free" replace />;
    return children;
  };

  const ProtectedAdminRoute = ({ children }) => {
    if (!authToken) return <Navigate to="/admin/login" replace />;
    return children;
  };

  const LogoutRoute = () => {
    logout();
    return <Navigate to="/free" replace />;
  };

  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Navigate to={isLoggedIn ? '/dashboard' : '/free'} replace />} />
        <Route path="/landing" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        <Route path="/login" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/signup" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <SignUp />} />
        <Route
          path="/free"
          element={isLoggedIn ? <Navigate to="/dashboard" replace /> : (
            <AppShell showSidebar={false}>
              <MPFlowPage freeMode={true} />
            </AppShell>
          )}
        />
        <Route
          path="/free/chat/:creativeId"
          element={isLoggedIn ? <Navigate to="/dashboard" replace /> : (
            <AppShell showSidebar={false}>
              <CreativeChatPage />
            </AppShell>
          )}
        />
        <Route path="/signup-required" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <SignupGatePage />} />
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        <Route path="/auth/facebook/callback" element={<FacebookAuthCallback />} />
        <Route path="/logout" element={<LogoutRoute />} />

        {/* MP Flow — acessível sem login (onboarding zero-login) */}
        <Route
          path="/mp"
          element={
            <AppShell showSidebar={isLoggedIn}>
              <MPFlowPage />
            </AppShell>
          }
        />

        <Route
          element={(
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          )}
        >
          <Route path="/home" element={<Navigate to="/mp" replace />} />
          <Route path="/manual-create" element={<ManualCreatePage />} />
          <Route path="/chat/:creativeId" element={<CreativeChatPage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/edit-image" element={<EditImagePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/contents" element={<ContentsPage />} />
          <Route path="/scheduling" element={<SchedulingPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaign-launch" element={<CampaignLaunchPage />} />
          <Route path="/campaign/:id/dashboard" element={<CampaignDashboardPage />} />
          <Route path="/mp/dashboard/:campaignId" element={<MPDashboardPage />} />
          <Route path="/campaign-flow" element={<CampaignFlowPage />} />
          <Route path="/mp/dashboard" element={<CampaignsDashboardPage />} />
          <Route path="/specialist" element={<SpecialistPanelPage />} />
          <Route path="/crm" element={<CRMPanelPage />} />
          <Route path="/landing-builder" element={<LandingPageBuilderPage />} />
          <Route path="/admin/niche" element={<AdminNicheConfigPage />} />
          <Route path="/brand-identity" element={<BrandIdentityPage />} />
          <Route path="/train-ai" element={<TrainAIPage />} />
          <Route path="/journey" element={<JourneyMapPage />} />
          <Route path="/3x" element={<Venda3xPage />} />
          <Route path="/meta-ads" element={<MetaAdsPage />} />
          <Route path="/ads-manager" element={<AdsManagerPage />} />
          <Route path="/ads/create" element={<AdsCreatePage />} />
          <Route path="/ads/connect" element={<AdsConnectPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/help" element={<HelpPage />} />

          <Route path="/conteudos" element={<Navigate to="/contents" replace />} />
          <Route path="/agendamento" element={<Navigate to="/scheduling" replace />} />
          <Route path="/identidade" element={<Navigate to="/brand-identity" replace />} />
          <Route path="/configuracoes" element={<Navigate to="/settings" replace />} />
        </Route>

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={(
            <ProtectedAdminRoute>
              <AdminPanel />
            </ProtectedAdminRoute>
          )}
        />

        <Route path="*" element={<Navigate to={isLoggedIn ? '/dashboard' : '/free'} replace />} />
      </Routes>

      {!isAdminRoute && <FloatingChat />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider delay={300} mobileTap>
          <Router>
            <AppContent />
          </Router>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
